/* emg makes art: header float, catalog scraps, drawer, crayon footer, inquiry prefill. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Header floats over the work once the page scrolls. Its height is shared as
     --emg-header-h so the home wall can run up behind it. */
  function initHeader() {
    const header = document.querySelector('[data-emg-header]');
    if (header && 'ResizeObserver' in window) {
      new ResizeObserver(([entry]) => {
        document.documentElement.style.setProperty('--emg-header-h', `${Math.round(entry.borderBoxSize?.[0]?.blockSize ?? header.offsetHeight)}px`);
      }).observe(header);
    }
    const main = document.getElementById('MainContent');
    if (!header || !main || !('IntersectionObserver' in window)) return;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;margin-bottom:-1px;';
    main.prepend(sentinel);
    new IntersectionObserver(([entry]) => {
      header.toggleAttribute('data-floating', !entry.isIntersecting);
    }).observe(sentinel);
  }

  /* Catalog: the four scraps fall in under the Catalog scrap. */
  function initCatalog() {
    document.querySelectorAll('[data-emg-catalog]').forEach((item) => {
      const button = item.querySelector('[data-emg-catalog-toggle]');
      if (!button) return;
      let closeTimer;
      let hoverOpenedAt = 0;

      const setOpen = (open) => {
        clearTimeout(closeTimer);
        item.toggleAttribute('data-open', open);
        button.setAttribute('aria-expanded', String(open));
      };

      button.addEventListener('click', () => {
        // A mouse that just opened the menu by hovering should not close it again by clicking.
        if (Date.now() - hoverOpenedAt < 700) return setOpen(true);
        setOpen(button.getAttribute('aria-expanded') !== 'true');
      });

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !finePointer.matches) return;
        if (!item.hasAttribute('data-open')) hoverOpenedAt = Date.now();
        setOpen(true);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        closeTimer = setTimeout(() => setOpen(false), 220);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setOpen(false);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && item.hasAttribute('data-open')) {
          setOpen(false);
          button.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!item.contains(event.target)) setOpen(false);
      });
    });
  }

  /* Mobile drawer: a native modal dialog keeps focus and Escape behavior honest. */
  function initDrawer() {
    const drawer = document.querySelector('[data-emg-drawer]');
    if (!drawer || typeof drawer.showModal !== 'function') return;
    document.querySelectorAll('[data-emg-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => {
        drawer.showModal();
        button.setAttribute('aria-expanded', 'true');
      });
    });
    drawer.querySelectorAll('[data-emg-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => drawer.close());
    });
    drawer.addEventListener('close', () => {
      document.querySelectorAll('[data-emg-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) drawer.close();
    });
  }

  /* The crayon lettering in the footer draws itself in when it comes into view. */
  function initCrayon() {
    const crayon = document.querySelector('[data-draw]');
    if (!crayon) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      crayon.setAttribute('data-draw', 'done');
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        crayon.setAttribute('data-draw', 'done');
        observer.disconnect();
      },
      { threshold: 0.35 }
    );
    observer.observe(crayon);
  }

  /* Quantity stepper on purchasable works. */
  function initQuantity() {
    document.querySelectorAll('[data-emg-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('[data-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.step));
          input.value = String(next);
        });
      });
    });
  }

  /* Variant select keeps the price, availability, hidden id and lead photograph in step. */
  function initVariants() {
    const twoColumn = window.matchMedia('(min-width: 900px)');
    document.querySelectorAll('[data-emg-variants]').forEach((form) => {
      const select = form.querySelector('[data-emg-variant-select]');
      const dataEl = form.querySelector('script[type="application/json"]');
      if (!select || !dataEl) return;
      const variants = JSON.parse(dataEl.textContent);
      const price = document.querySelector('[data-emg-price]');
      const submit = form.querySelector('[type="submit"]');
      const media = document.querySelector('[data-emg-media]');

      /* The frame's photograph moves to the head of the stack. Beside the label, the stack
         scrolls back up to it; on a phone the label sits below, so the page stays put. */
      function lead(mediaId) {
        const item = media && mediaId && media.querySelector(`[data-media-id="${mediaId}"]`);
        if (!item || item.style.order === '-1') return;
        media.querySelectorAll('[data-media-id]').forEach((li) => li.style.removeProperty('order'));
        item.style.order = '-1';
        const img = item.querySelector('img');
        if (img) img.loading = 'eager';
        if (!reduceMotion.matches) {
          item.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 280, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
        }
        if (twoColumn.matches && media.getBoundingClientRect().top < 0) {
          media.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }

      select.addEventListener('change', () => {
        const variant = variants.find((v) => String(v.id) === select.value);
        if (!variant) return;
        lead(variant.media);
        if (price) price.textContent = variant.price;
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? submit.dataset.labelAdd : submit.dataset.labelSoldOut;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      });
    });
  }

  /* Contact: arriving from "Inquire about this work" names the piece in the form. */
  async function initInquiry() {
    const form = document.querySelector('[data-emg-inquiry]');
    if (!form) return;
    const params = new URLSearchParams(window.location.search);
    const topic = form.querySelector('[name="contact[topic]"]');
    if (topic && params.get('topic') === 'commission') topic.value = 'Commission';

    const handle = params.get('work');
    if (!handle || !/^[a-z0-9-]+$/.test(handle)) return;
    if (topic) topic.value = 'Original artwork inquiry';

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return;
      const product = await response.json();
      const slot = form.querySelector('[data-emg-inquiry-work]');
      const field = form.querySelector('[name="contact[work]"]');
      if (field) field.value = `${product.title} (${window.location.origin}/products/${handle})`;
      if (slot) {
        const img = slot.querySelector('img');
        const title = slot.querySelector('[data-emg-inquiry-title]');
        if (img && product.featured_image) {
          img.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=160`;
          img.alt = '';
        }
        if (title) title.textContent = product.title;
        slot.hidden = false;
      }
      const message = form.querySelector('[name="contact[body]"]');
      if (message && !message.value) {
        message.placeholder = `I'd love to know more about "${product.title}": availability, framing and shipping to…`;
      }
    } catch (error) {
      /* The form still works without the preview. */
    }
  }

  /* The artist's smiley trails the pointer on a soft spring and wiggles with speed.
     The real pointer stays, so clicks land exactly where the visitor aims. */
  function initSmiley() {
    if (!finePointer.matches) return;
    const smiley = document.createElement('div');
    smiley.className = 'emg-smiley';
    smiley.setAttribute('aria-hidden', 'true');
    document.body.append(smiley);

    const OFFSET = 18; // sits just below-right of the pointer tip, off the click target
    const pointer = { x: -100, y: -100 };
    const pos = { x: -100, y: -100 };
    let rot = 0;
    let rotVel = 0;
    let scale = 1;
    let targetScale = 1;
    let lastTime = 0;
    let running = false;
    let placed = false;

    const frame = (time) => {
      const dt = Math.min(64, time - (lastTime || time)) / 16.667;
      lastTime = time;
      const still = reduceMotion.matches;

      const prevX = pos.x;
      const follow = still ? 1 : 1 - Math.pow(1 - 0.16, dt);
      pos.x += (pointer.x + OFFSET - pos.x) * follow;
      pos.y += (pointer.y + OFFSET - pos.y) * follow;

      if (still) {
        rot = 0;
        scale = targetScale;
      } else {
        // Lean into the direction of travel, then wobble back to upright on a spring.
        const vx = (pos.x - prevX) / Math.max(dt, 0.001);
        const lean = Math.max(-28, Math.min(28, vx * 1.6));
        rotVel += (lean - rot) * 0.14 * dt;
        rotVel *= Math.pow(0.78, dt);
        rot += rotVel * dt;
        scale += (targetScale - scale) * (1 - Math.pow(1 - 0.2, dt));
      }

      smiley.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) rotate(${rot.toFixed(2)}deg) scale(${scale.toFixed(3)})`;

      const settled =
        Math.abs(pointer.x + OFFSET - pos.x) < 0.1 &&
        Math.abs(pointer.y + OFFSET - pos.y) < 0.1 &&
        Math.abs(rot) < 0.05 &&
        Math.abs(rotVel) < 0.05 &&
        Math.abs(targetScale - scale) < 0.002;
      if (settled) {
        running = false;
        lastTime = 0;
        return;
      }
      requestAnimationFrame(frame);
    };

    const kick = () => {
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    };

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        if (!placed) {
          pos.x = pointer.x + OFFSET;
          pos.y = pointer.y + OFFSET;
          placed = true;
        }
        smiley.toggleAttribute('data-visible', true);
        const overControl = event.target.closest?.('a, button, summary, label, select, [role="button"]');
        targetScale = overControl ? 1.3 : 1;
        kick();
      },
      { passive: true }
    );
    document.documentElement.addEventListener('pointerleave', () => smiley.removeAttribute('data-visible'));
    document.addEventListener('pointerdown', () => {
      targetScale = 0.8;
      kick();
    });
    document.addEventListener('pointerup', () => {
      targetScale = 1;
      kick();
    });
  }

  const init = () => {
    initSmiley();
    initHeader();
    initCatalog();
    initDrawer();
    initCrayon();
    initQuantity();
    initVariants();
    initInquiry();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
